import d3 from 'd3';
import {area as d3Area, line as d3Line} from 'd3-shape';
import moment from 'moment';
import d3_lucid from 'common/charts/d3-lucid';
import * as AnalyticAppColor from 'common/constants/analytic-app-color';
import {
  AnalyticAppRendererEnum,
  DateTimeFormat,
  ReportingPeriodEnum,
} from 'common/proxies/enum-proxy';
import {DEFAULT_DURATION, DELAY, LONG} from 'common/constants';
import {formatDate} from 'common/formatters/date-formatter';
import {formatNumber} from 'common/formatters/number-formatter';
import {getDateFormatVerbose, getEndOfPeriod} from 'common/utils/date-util';
import {getTranslate} from 'common/utils/dom-util';
import {isOdd, isValid} from 'common/utils/number-util';

const minimalTimeseriesChart = function () {
  // Properties

  let data = [];
  let height = 526;
  let message = '';
  let period = ReportingPeriodEnum.TODAY;
  let selection;
  let state = d3_lucid.states.INIT;
  let width = 1170;

  function chart() {
    // States

    const states = {
      // Appends a background and a spinner:
      init(params) {
        appendSpinner(params.selection);
      },
      // Fades existing content and appends a spinner:
      loading(params) {
        d3_lucid.svg.removeSpinner(params.selection);
        d3_lucid.svg.removeByID(d3_lucid.id.SVG_DIV);
        appendSpinner(params.selection);
      },
      // Fades existing content (if any) and appends a message:
      message(params) {
        d3_lucid.svg.removeSpinner(params.selection);
        d3_lucid.svg.removeByID(d3_lucid.id.SVG_DIV);
        params.margins = getMargins();
        params.div = appendDiv(params.selection, params.width, params.height);
        params.svg = appendRoot(params.div, params.width, params.height);
        appendMessage(
          params.svg,
          params.width,
          params.height,
          params.margins,
          params.message,
        );
      },
      // Appends a chart:
      normal(params) {
        d3_lucid.svg.removeSpinner(params.selection);
        d3_lucid.svg.removeByID(d3_lucid.id.SVG_DIV);
        params.margins = getMargins();
        params.padding = getPadding(params.width);
        params.contentHeight = d3_lucid.getContentHeight(
          params.height,
          params.margins,
        );
        params.contentWidth = d3_lucid.getContentWidth(
          params.width,
          params.margins,
        );
        const mainSeries = params.data[0];
        params.numColumns = getNumColumns(params, mainSeries);
        params.domainX = d3_lucid.getTimeDomain(
          mainSeries.start,
          mainSeries.end,
          params.period,
        );
        params.domainY = getDomainY(params);
        params.rangeY = d3_lucid.getRangeY(
          params.contentHeight,
          params.padding,
        );
        params.scaleY = d3_lucid.getLinearScale(params.domainY, params.rangeY);
        params.div = appendDiv(params.selection, params.width, params.height);
        params.svg = appendRoot(
          params.div,
          params.width,
          params.height,
          params.margins,
        );
        params.defs = d3_lucid.svg.append('defs', params.svg, [
          {
            id: d3_lucid.id.DEFS,
          },
        ]);
        params.container = appendContainer(params.svg, params.padding);
        params.legendContainer = appendLegendContainer(params.svg);
        for (let ii = 0, nn = params.data.length; ii < nn; ii++) {
          const series = params.data[ii];
          series.columnSpacing = getColumnSpacing();
          series.columnWidth = getColumnWidth(params);
          series.domainX = getDomainX(params, series, ii);
          series.domainY = params.domainY;
          series.index = ii;
          series.transitions = [];
          series.rangeX = d3_lucid.getRangeX(
            params.contentWidth,
            params.padding,
          );
          series.rangeY = params.rangeY;
          series.scaleX = d3_lucid.getTimeScale(series.domainX, series.rangeX);
          series.scaleY = params.scaleY;
          series.ticksX = getTicksX(params);
          series.ticksY = getTicksY(series);
          series.tickPosX = getTickPosX(params, series);
          series.tickFormatX = getTickFormatX(params);
          series.axisX = getAxisX(series);
          series.area = getArea(series);
          series.line = getLine(series);
          series.area = appendArea(params.container, series, params.height);
          series.columns = appendColumns(params.container, series, params);
          series.path = appendLine(params.container, series);
          series.plot = appendPlot(params.container, series, params);
          if (ii === 0) {
            appendGridlines(params.container, series, params);
            appendAxisX(series, params);
          }
          series.legend = appendLegend(params.legendContainer, series, ii);
          series.marker = appendMarker(
            params.container,
            series,
            params.svg,
            ii,
          );
        }
        playTransitions(params.data);
      },
    };

    // Set state

    states[state]({
      data,
      height,
      message,
      period,
      selection,
      state,
      width,
    });

    // Functions

    function appendArea(container, series, height) {
      if (series.area) {
        const clipPathID = `areaClipPath${series.index}`;
        const defs = d3_lucid.svg.selectByID(d3_lucid.id.DEFS);
        const clipRect = defs
          .append('clipPath')
          .attr('id', clipPathID)
          .append('rect')
          .attr('height', height)
          .attr('width', 0);
        series.transitions.push(() => {
          clipRect
            .transition()
            .attr('width', () => {
              return series.scaleX(series.end);
            })
            .duration(LONG)
            .each('end', () => {
              d3_lucid.svg.removeByID(clipPathID);
            });
        });
        return d3_lucid.svg.append('path', container, [
          {
            'clip-path': `url(#${clipPathID})`,
            d: series.area(series.values),
          },
          {
            fill: series.fill,
            'fill-opacity': 0.5,
          },
        ]);
      }
    }

    function appendAxisX(series, params) {
      const svg = params.svg;
      const contentHeight = params.contentHeight;
      const columnWidth = series.columnWidth;
      const format = getDateFormatVerbose(params.period);
      const scaleX = series.scaleX;

      svg
        .append('g')
        .attr('transform', getTranslate(0, contentHeight))
        .call(series.axisX)
        .selectAll('text')
        .attr('class', (datum, index) => {
          if (isOdd(index)) {
            return 'graph-axis-label-odd';
          }
        })
        .attr('x', () => {
          return columnWidth / 2;
        })
        .style('text-anchor', () => {
          return 'middle';
        });
      const tooltipHeight = params.margins.bottom;
      const tooltip = svg
        .append('g')
        .attr('id', 'axisTooltipX')
        .style('display', 'none')
        .style('pointer-events', 'none');
      const background = tooltip
        .append('rect')
        .attr('fill', AnalyticAppColor.BLACK.primary)
        .attr('height', tooltipHeight)
        .attr('opacity', 0.75);
      const date = tooltip
        .append('text')
        .attr('class', 'axis-tooltip-text')
        .attr('dominant-baseline', 'middle')
        .attr('font-weight', 'bold')
        .attr('height', tooltipHeight)
        .attr('text-anchor', 'middle')
        .attr('y', () => {
          return tooltipHeight * 0.5 + 1;
        });

      const padding = 16;
      const tooltipY = params.height - tooltipHeight;
      svg.on('mousemove.axisTooltipX', function () {
        const mouse = d3.mouse(this);
        const mouseX = mouse[0];
        if (mouseX > 0) {
          let timestamp = scaleX.invert(mouseX);
          timestamp = formatDate(timestamp, format);
          date.text(timestamp);
          const dateWidth = date.node().getComputedTextLength();
          const backgroundWidth = dateWidth + padding;
          let tooltipX = mouseX;
          const min = backgroundWidth * 0.5;
          if (mouseX < min) {
            tooltipX = min;
          }
          const max =
            params.width - params.margins.left - params.margins.right - min;
          if (mouseX > max) {
            tooltipX = max;
          }
          background.attr('width', backgroundWidth).attr('x', -min);
          const translate = getTranslate(tooltipX, tooltipY);
          tooltip.attr('transform', translate).style('display', 'inherit');
        } else {
          tooltip.style('display', 'none');
        }
      });
      svg.on('mouseout.axisTooltipX', () => {
        tooltip.style('display', 'none');
      });
    }

    function appendColumns(container, series, params) {
      const renderer = series.renderer.slug;
      if (renderer === AnalyticAppRendererEnum.COLUMN.slug) {
        const contentHeight = params.contentHeight;
        const columnWidth = series.columnWidth;
        const spacing = series.columnSpacing;
        const scaleX = series.scaleX;
        const scaleY = series.scaleY;
        const unit = series.unit.shortName;
        const columns = container
          .selectAll('.column')
          .data(series.values)
          .enter()
          .append('rect')
          .attr('fill', series.fill)
          .attr('fill-opacity', 0)
          .attr('height', 0)
          .attr('width', () => {
            return columnWidth - spacing;
          })
          .attr('x', (datum) => {
            return scaleX(datum.timestamp);
          })
          .attr('y', () => {
            return contentHeight - params.padding.top;
          })
          .on('mouseout', function () {
            d3.select(this).attr('fill-opacity', 0.5);
          })
          .on('mouseover', function () {
            d3.select(this).attr('fill-opacity', 0.85);
          });
        const values = series.values;
        const bisect = d3.bisector((datum) => {
          return datum.timestamp;
        }).right;
        params.svg.on('mousemove.column', function () {
          const mouse = d3.mouse(this);
          const inverted = scaleX.invert(mouse[0]);
          const position = bisect(values, inverted) - 1;
          if (values[position]) {
            const value = values[position].value;
            updateLegend(
              series.legend,
              series.legendReading,
              value,
              unit,
              0,
              series.legendItem,
            );
          } else {
            updateLegend(series.legend, series.legendReading);
          }
        });
        params.svg.on('mouseout.column', () => {
          updateLegend(series.legend, series.legendReading);
        });

        series.transitions.push(() => {
          columns
            .transition()
            .duration(DEFAULT_DURATION)
            .delay((datum, index) => {
              return DELAY * index;
            })
            .ease(d3.ease('quad-out'))
            .attr('fill-opacity', 0.5)
            .attr('height', (datum) => {
              if (isValid(datum.value)) {
                const top = params.padding.top;
                const posY = scaleY(datum.value);
                return contentHeight - top - posY;
              }
            })
            .attr('y', (datum) => {
              if (isValid(datum.value)) {
                const bottom = params.margins.bottom;
                const height = contentHeight - bottom - scaleY(datum.value);
                return contentHeight - bottom - height;
              }
            });
        });
        return columns;
      }
    }

    function appendContainer(selection, padding) {
      return d3_lucid.svg.group(selection, [
        {
          id: 'container',
          transform: getTranslate(0, padding.top),
        },
      ]);
    }

    function appendDiv(selection, width, height) {
      return d3_lucid.svg.div(selection, [
        {
          height,
          id: d3_lucid.id.SVG_DIV,
          width,
        },
      ]);
    }

    function appendGridlines(container, series, params) {
      return container
        .append('g')
        .attr('id', 'gridlines')
        .selectAll('.gridline')
        .data(series.ticksY)
        .enter()
        .append('line')
        .attr('fill', 'none')
        .attr('shape-rendering', 'crispEdges')
        .attr('stroke', series.stroke)
        .attr('stroke-opacity', 0.25)
        .attr('stroke-width', '1px')
        .attr('x1', 0)
        .attr('x2', params.width)
        .attr('y1', (datum) => {
          return series.scaleY(datum);
        })
        .attr('y2', (datum) => {
          return series.scaleY(datum);
        })
        .style('pointer-events', 'none');
    }

    function appendLegend(container, series, index) {
      series.legendItem = d3_lucid.svg.append('g', container, [
        {
          id: `legendItem${index}`,
          transform: getTranslate(0, 0),
        },
        {
          display: 'none',
          'pointer-events': 'none',
        },
      ]);
      const iconWidth = 48;
      if (series.renderer.slug === AnalyticAppRendererEnum.PLOT.slug) {
        d3_lucid.svg.append('circle', series.legendItem, [
          {
            cx: Math.ceil(iconWidth * 0.75),
            cy: 0,
            r: 7,
          },
          {
            fill: series.fill,
            'fill-opacity': 0.8,
            stroke: AnalyticAppColor.WHITE.primary,
            'stroke-width': '2px',
          },
        ]);
      } else {
        const line = d3_lucid.svg.append('line', series.legendItem, [
          {
            x1: 0,
            x2: iconWidth,
            y1: 0,
            y2: 0,
          },
          {
            stroke: series.stroke,
            'stroke-width': '3px',
          },
        ]);
        if (series.strokeStyle === 'dashed') {
          d3_lucid.svg.setStyles(line, {
            'stroke-dasharray': '6,2',
            'stroke-width': '2px',
          });
        }
      }
      series.legendReading = d3_lucid.svg.append('text', series.legendItem, [
        {
          id: `legendReading${index}`,
          x: iconWidth + 8,
        },
        {
          'dominant-baseline': 'middle',
        },
      ]);
      return series.legendItem;
    }

    function appendLegendContainer(selection) {
      return d3_lucid.svg.group(selection, [
        {
          class: 'legend',
          id: 'legend',
          transform: getTranslate(20, 20),
        },
      ]);
    }

    function appendLine(container, series) {
      if (series.line) {
        const clipPathID = `lineClipPath${series.index}`;
        const defs = d3_lucid.svg.selectByID(d3_lucid.id.DEFS);
        const clipRect = defs
          .append('clipPath')
          .attr('id', clipPathID)
          .append('rect')
          .attr('height', height)
          .attr('width', 0);
        const line = d3_lucid.svg.append('path', container, [
          {
            'clip-path': `url(#${clipPathID})`,
            d: series.line(series.values),
          },
          {
            fill: 'none',
            stroke: series.stroke,
            'stroke-width': '3px',
          },
        ]);
        if (series.strokeStyle === 'dashed') {
          d3_lucid.svg.setStyles(line, {
            'stroke-dasharray': '6,2',
            'stroke-width': '2px',
          });
        }
        series.transitions.push(() => {
          clipRect
            .transition()
            .attr('width', () => {
              return series.scaleX(series.end);
            })
            .duration(LONG)
            .each('end', () => {
              d3_lucid.svg.removeByID(clipPathID);
            });
        });
        return line;
      }
    }

    function appendMarker(container, series, svg, index) {
      if (series.line) {
        const marker = d3_lucid.svg.append('circle', container, [
          {
            r: 5,
          },
          {
            display: 'none',
            'fill-opacity': 0,
            stroke: series.stroke,
            'stroke-width': '3px',
          },
        ]);
        const points = d3_lucid.svg.getPointsFromPath(series.path);
        const bisect = d3.bisector((datum) => {
          return datum.x;
        }).right;
        const unit = series.unit.shortName;
        svg.on(`mousemove.${index}`, function () {
          const mouse = d3.mouse(this);
          const position = bisect(points, mouse[0]);
          const point = points[position];
          if (point) {
            const difference = point.x - mouse[0];
            if (difference <= 1) {
              const valueY = series.scaleY.invert(point.y);
              updateLegend(
                series.legend,
                series.legendReading,
                valueY,
                unit,
                index,
                series.legendItem,
              );
              updateMarker(marker, point.x, point.y);
            } else {
              updateLegend(series.legend, series.legendReading);
              updateMarker(marker);
            }
          } else {
            updateLegend(series.legend, series.legendReading);
            updateMarker(marker);
          }
        });
        svg.on(`mouseout.${index}`, () => {
          updateLegend(series.legend, series.legendReading);
          updateMarker(marker);
        });
      }
    }

    function appendMessage(selection, width, height, margins, message) {
      const contentWidth = d3_lucid.getContentWidth(width, margins);
      const contentHeight = d3_lucid.getContentHeight(height, margins);
      const center = d3_lucid.getCenter(contentWidth, contentHeight);
      return d3_lucid.svg
        .append('text', selection, [
          {
            x: center.x,
            y: center.y,
          },
          {
            'dominant-baseline': 'middle',
            'font-size': '18px',
            'text-anchor': 'middle',
          },
        ])
        .text(message);
    }

    function appendRoot(selection, width, height, margins) {
      const computedWidth = margins
        ? width - margins.left - margins.right
        : width;
      return d3_lucid.svg.append('svg', selection, [
        {
          height,
          id: d3_lucid.id.SVG,
          width: computedWidth,
        },
      ]);
    }

    function appendPlot(container, series, params) {
      const renderer = series.renderer.slug;
      if (renderer === AnalyticAppRendererEnum.PLOT.slug) {
        const contentHeight = params.contentHeight;
        const columnWidth = series.columnWidth;
        const scaleX = series.scaleX;
        const scaleY = series.scaleY;
        const values = series.values;
        const plotPoints = container
          .selectAll('.plot-point')
          .data(values)
          .enter()
          .append('circle')
          .attr('class', 'plot-point')
          .attr('cx', (datum) => {
            return Math.floor(scaleX(datum.timestamp) + columnWidth / 2);
          })
          .attr('cy', (datum) => {
            return scaleY(datum.value) + contentHeight * 0.1;
          })
          .attr('fill', series.fill)
          .attr('fill-opacity', 0.8)
          .attr('r', () => {
            let radius = columnWidth / 9;
            if (radius < 5) {
              radius = 5;
            }
            if (radius > 10) {
              radius = 10;
            }
            return radius;
          })
          .attr('stroke', AnalyticAppColor.WHITE.primary)
          .attr('stroke-width', 2)
          .style('display', (datum) => {
            if (!isValid(datum.value)) {
              return 'none';
            }
          })
          .style('opacity', 0);
        series.transitions.push(() => {
          plotPoints
            .transition()
            .duration(DEFAULT_DURATION)
            .delay((datum, index) => {
              return DELAY * index;
            })
            .ease(d3.ease('quad-out'))
            .attr('cy', (datum) => {
              return scaleY(datum.value);
            })
            .style('opacity', 1);
        });
        const unit = series.unit.shortName;
        const bisect = d3.bisector((datum) => {
          return datum.timestamp;
        }).right;
        params.svg.on('mousemove.plot', function () {
          const mouse = d3.mouse(this);
          const inverted = scaleX.invert(mouse[0]);
          const position = bisect(values, inverted) - 1;
          if (values[position]) {
            const value = values[position].value;
            updateLegend(
              series.legend,
              series.legendReading,
              value,
              unit,
              1,
              series.legendItem,
            );
          } else {
            updateLegend(series.legend, series.legendReading);
          }
        });
        params.svg.on('mouseout.plot', () => {
          updateLegend(series.legend, series.legendReading);
        });
        return plotPoints;
      }
    }

    function appendSpinner(selection) {
      d3_lucid.svg.spinner(selection, {
        color: AnalyticAppColor.WHITE.primary,
      });
    }

    function getArea(series) {
      const renderer = series.renderer.slug;
      if (renderer === AnalyticAppRendererEnum.AREA.slug) {
        return d3Area()
          .defined((datum) => {
            if (isValid(datum.value)) {
              return true;
            }
          })
          .curve(series.interpolate)
          .x((datum) => {
            return series.scaleX(datum.timestamp);
          })
          .y0(series.rangeY[0])
          .y1((datum) => {
            return series.scaleY(datum.value);
          });
      }
    }

    function getAxisX(series) {
      return d3.svg
        .axis()
        .scale(series.scaleX)
        .orient('bottom')
        .tickFormat((datum) => {
          return formatDate(datum, series.tickFormatX);
        })
        .ticks(series.ticksX.unit, series.ticksX.interval)
        .tickSize(0)
        .tickPadding(9);
    }

    function getColumnSpacing() {
      return 2;
    }

    function getColumnWidth(params) {
      return (
        (params.width - params.margins.left - params.margins.right) /
        params.numColumns
      );
    }

    function getDomainX(params, series, index) {
      const endOfPeriod = getEndOfPeriod(series.start, params.period, false);
      const domainX = [series.start, endOfPeriod];
      if (!index) {
        return domainX;
      }
      if (params.period.slug !== ReportingPeriodEnum.MONTH.slug) {
        return domainX;
      }

      // This case is a time domain for a previous month:
      const mainDomainX = params.domainX;
      const mainStart = moment(mainDomainX[0]);
      const mainEnd = moment(mainDomainX[1]);
      const seriesStart = moment(domainX[0]);
      const adjustedSeriesEnd = moment(seriesStart);
      adjustedSeriesEnd.add(mainEnd.diff(mainStart), 'ms');
      return [seriesStart, adjustedSeriesEnd];
    }

    function getDomainY(params) {
      const data = params.data;
      let min = Number.MAX_VALUE;
      let max = Number.MIN_VALUE;
      for (let ii = 0, nn = data.length; ii < nn; ii++) {
        const series = data[ii];
        const values = series.values;
        if (values && values.length) {
          const extent = d3.extent(values, (datum) => {
            return datum.value;
          });
          if (extent[0] < min) {
            min = extent[0];
          }
          if (extent[1] > max) {
            max = extent[1];
          }
        }
      }
      if (min > 0) {
        min = 0;
      }
      if (min === Number.MAX_VALUE) {
        min = 0;
      }
      if (max === Number.MIN_VALUE) {
        max = 0;
      }
      return [min, max];
    }

    function getLine(series) {
      const renderer = series.renderer.slug;
      if (
        renderer === AnalyticAppRendererEnum.AREA.slug ||
        renderer === AnalyticAppRendererEnum.LINE.slug
      ) {
        return d3Line()
          .defined((datum) => {
            if (isValid(datum.value)) {
              return true;
            }
          })
          .curve(series.interpolate)
          .x((datum) => {
            return series.scaleX(datum.timestamp);
          })
          .y((datum) => {
            return series.scaleY(datum.value);
          });
      }
    }

    function getMargins() {
      return {
        top: 0,
        right: 20,
        bottom: 28,
        left: 20,
      };
    }

    function getNumColumns(params, series) {
      switch (params.period.slug) {
        case ReportingPeriodEnum.TODAY.slug:
          return 24;
        case ReportingPeriodEnum.WEEK.slug:
          return 7;
        case ReportingPeriodEnum.MONTH.slug:
          return moment(series.start).daysInMonth();
        case ReportingPeriodEnum.YEAR.slug:
          return 18;
        default:
          return 1;
      }
    }

    function getPadding(width) {
      let top = 40;
      if (width < 700 && width >= 500) {
        top = Math.ceil((top / 3) * 2);
      }
      if (width < 500) {
        top = Math.ceil(top / 3);
      }
      return {
        top,
        right: 0,
        bottom: 0,
        left: 0,
      };
    }

    function getTickFormatX(params) {
      switch (params.period.slug) {
        case ReportingPeriodEnum.TODAY.slug:
          return DateTimeFormat.ISO_TIME;
        case ReportingPeriodEnum.WEEK.slug:
          return DateTimeFormat.DAY_OF_WEEK;
        case ReportingPeriodEnum.MONTH.slug:
          return DateTimeFormat.DAY;
        case ReportingPeriodEnum.YEAR.slug:
          return DateTimeFormat.MONTH;
        default:
          return DateTimeFormat.TEXTUAL_DAY_OF_WEEK;
      }
    }

    function getTickPosX(params, series) {
      let posX = series.columnWidth * 0.5;
      posX -= series.columnSpacing * 0.5;
      switch (params.period.slug) {
        case ReportingPeriodEnum.MONTH.slug:
          return posX;
        case ReportingPeriodEnum.YEAR.slug:
          return posX;
        default:
          return 0;
      }
    }

    function getTicksX(params) {
      const period = params.period.slug;
      let interval;
      let unit;
      switch (period) {
        case ReportingPeriodEnum.TODAY.slug:
          interval = 3;
          unit = d3.time.hours;
          break;
        case ReportingPeriodEnum.WEEK.slug:
          interval = 1;
          unit = d3.time.days;
          break;
        case ReportingPeriodEnum.MONTH.slug:
          interval = 1;
          unit = d3.time.days;
          break;
        case ReportingPeriodEnum.YEAR.slug:
          interval = 1;
          unit = d3.time.months;
          break;
        default:
          break;
      }
      return {
        interval,
        unit,
      };
    }

    function getTicksY(series) {
      return series.scaleY.ticks(4);
    }

    function playTransitions(series) {
      for (let ii = 0, nn = series.length; ii < nn; ii++) {
        const transitions = series[ii].transitions;
        for (let jj = 0, mm = transitions.length; jj < mm; jj++) {
          transitions[jj]();
        }
      }
    }

    function updateLegend(legend, reading, value, unit, index, item) {
      if (isValid(value) && unit) {
        const formatted = formatNumber(value);
        reading.text(`${formatted} ${unit}`);
        if (index) {
          const firstItem = d3_lucid.svg.selectByID('legendItem0');
          const firstWidth = firstItem.node().getBBox().width;
          item.attr('transform', getTranslate(firstWidth + 40, 0));
        }
        legend.style('display', 'inline');
      } else {
        legend.style('display', 'none');
      }
    }

    function updateMarker(marker, posX, posY) {
      if (isValid(posX) && isValid(posY)) {
        marker.attr('cx', posX);
        marker.attr('cy', posY);
        marker.style('display', 'inline');
      } else {
        marker.style('display', 'none');
      }
    }
  }

  // Getters and setters

  chart.data = function (value) {
    if (value === undefined) {
      return data;
    }
    data = value;
    return chart;
  };

  chart.height = function (value) {
    if (value === undefined) {
      return height;
    }
    height = value;
    return chart;
  };

  chart.message = function (value) {
    if (value === undefined) {
      return message;
    }
    message = value;
    return chart;
  };

  chart.period = function (value) {
    if (value === undefined) {
      return period;
    }
    period = value;
    return chart;
  };

  chart.selection = function (value) {
    if (value === undefined) {
      return selection;
    }
    selection = d3.select(value);
    return chart;
  };

  chart.state = function (value) {
    if (value === undefined) {
      return state;
    }
    state = value;
    return chart;
  };

  chart.width = function (value) {
    if (value === undefined) {
      return width;
    }
    width = value;
    return chart;
  };

  return chart;
};

export default minimalTimeseriesChart;
